import React, { useState } from 'react';

import { Button } from '@opendoor/mosaic';

import { SearchVisits_trippy_searchVisits_visits } from '../../graphql/athena/types/SearchVisits';
import { VisitsTab } from '../../pages/Visits';
import { prettifyAddress, prettifyCityStateZip, prettifyStreetAddress } from '../../utils/address';
import { prettifyDate } from '../../utils/date';
import HorizontalLayout from '../layouts/HorizontalLayout';
import VerticalLayout from '../layouts/VerticalLayout';
import AssignVisitModal from './AssignVisitModal';
import RejectVisitModal from './RejectVisitModal';

enum VisitTableColumn {
  ADDRESS = 'Address',
  CONTACT = 'Contact',
  DATE_REQUESTED = 'Date Requested',
  SCHEDULED_DATE = 'Scheduled Date',
  NEW_VISITS_ACTIONS = 'New Visits Actions',
  ASSIGNED_VISITS_ACTIONS = 'Assigned Visits Actions',
  ASSIGNED_TO = 'Assigned To',
  COMPLETED_DATE = 'Completed Date',
}

interface Column {
  id: VisitTableColumn;
  label: string;
  value: (
    visit: SearchVisits_trippy_searchVisits_visits,
    setAssignDialogVisit: React.Dispatch<
      React.SetStateAction<SearchVisits_trippy_searchVisits_visits | null>
    >,
    setRejectDialogVisit: React.Dispatch<
      React.SetStateAction<SearchVisits_trippy_searchVisits_visits | null>
    >,
  ) => any;
}

const columns: Column[] = [
  {
    id: VisitTableColumn.ADDRESS,
    label: 'Address',
    value: (visit, __, ___) => {
      if (!visit.address) return <div>No Address Found</div>;
      const prettyStreet = prettifyStreetAddress(visit.address);
      const prettyCity = prettifyCityStateZip(visit.address);

      return (
        <VerticalLayout>
          <div>{prettyStreet}</div>
          <div style={styles.cityStateZipText}>{prettyCity}</div>
        </VerticalLayout>
      );
    },
  },
  {
    id: VisitTableColumn.CONTACT,
    label: 'Contact',
    value: (visit, _, __) => {
      if (!visit.address) return <span>{'No Contact Found'}</span>;

      const fieldUser = visit.enrollment?.metadata?.default_field_user
        ? visit.enrollment?.metadata?.default_field_user.fullName
        : 'No Contact Found';
      return <span>{fieldUser}</span>;
    },
  },
  {
    id: VisitTableColumn.DATE_REQUESTED,
    label: 'Date Requested',
    value: (visit, __, ___) => (
      <span>{visit.createdAt ? prettifyDate(visit.createdAt, true) : 'No Date Found'}</span>
    ),
  },
  {
    id: VisitTableColumn.SCHEDULED_DATE,
    label: 'Scheduled Date',
    value: (visit, __, ___) => (
      <span>
        {visit.scheduledStartAt ? prettifyDate(visit.scheduledStartAt, true) : 'No Date Found'}
      </span>
    ),
  },
  {
    id: VisitTableColumn.COMPLETED_DATE,
    label: 'Completed Date',
    value: (visit, __, ___) => (
      <span>{visit.checkOutAt ? prettifyDate(visit.checkOutAt, true) : 'No Date Found'}</span>
    ),
  },
  {
    id: VisitTableColumn.ASSIGNED_TO,
    label: 'Assigned To',
    value: (visit, __, ___) => (
      <span>{visit.assignedUser ? visit.assignedUser.fullName : 'No Assignee Found'}</span>
    ),
  },
  {
    id: VisitTableColumn.NEW_VISITS_ACTIONS,
    label: 'Actions',
    value: (visit, setAssignDialogVisit, setRejectDialogVisit) => {
      return (
        <HorizontalLayout>
          <Button
            color="primary"
            style={{ marginRight: 10 }}
            onClick={() => setAssignDialogVisit(visit)}
          >
            Accept and Assign
          </Button>
          <Button onClick={() => setRejectDialogVisit(visit)}>Reject</Button>
        </HorizontalLayout>
      );
    },
  },
  {
    id: VisitTableColumn.ASSIGNED_VISITS_ACTIONS,
    label: 'Actions',
    value: (visit, setAssignDialogVisit, setRejectDialogVisit) => {
      return (
        <HorizontalLayout>
          <Button
            color="primary"
            style={{ marginRight: 10 }}
            onClick={() => setAssignDialogVisit(visit)}
          >
            Reassign
          </Button>
          <Button onClick={() => setRejectDialogVisit(visit)}>Reject</Button>
        </HorizontalLayout>
      );
    },
  },
];

interface VisitsListProps {
  visits: SearchVisits_trippy_searchVisits_visits[];
  selectedTab: string;
  addressDebounce: string;
}

const VisitsList = ({ visits, selectedTab, addressDebounce }: VisitsListProps) => {
  const [assignDialogVisit, setAssignDialogVisit] =
    useState<SearchVisits_trippy_searchVisits_visits | null>(null);
  const [rejectDialogVisit, setRejectDialogVisit] =
    useState<SearchVisits_trippy_searchVisits_visits | null>(null);

  const getCurrentColumns = () => {
    const baseColumns = [
      VisitTableColumn.ADDRESS,
      VisitTableColumn.CONTACT,
      VisitTableColumn.DATE_REQUESTED,
      VisitTableColumn.SCHEDULED_DATE,
    ];
    const newColumns = [...baseColumns, VisitTableColumn.NEW_VISITS_ACTIONS];
    const assignedColumns = [
      ...baseColumns,
      VisitTableColumn.ASSIGNED_TO,
      VisitTableColumn.ASSIGNED_VISITS_ACTIONS,
    ];
    const completedColumns = [
      ...baseColumns,
      VisitTableColumn.COMPLETED_DATE,
      VisitTableColumn.ASSIGNED_TO,
    ];

    if (selectedTab === VisitsTab.NEW) {
      return columns.filter((c) => newColumns.includes(c.id));
    } else if (selectedTab === VisitsTab.ASSIGNED) {
      return columns.filter((c) => assignedColumns.includes(c.id));
    } else {
      return columns.filter((c) => completedColumns.includes(c.id));
    }
  };
  const currentColumns = getCurrentColumns();

  const filteredVisits =
    addressDebounce.length > 0
      ? visits.filter(
          (v) => v.address && prettifyAddress(v.address).toLowerCase().includes(addressDebounce),
        )
      : visits;

  return (
    <VerticalLayout style={{ overflowY: 'scroll' }}>
      <table style={{ marginTop: 20, borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {currentColumns.map(({ label }, i) => {
              const headerStyle = {
                ...styles.columnHeader,
                ...(i === 0 ? { paddingLeft: 20 } : {}),
              };
              return (
                <th key={i} style={{ ...headerStyle, textAlign: 'left' }}>
                  <span style={styles.columnHeaderText}>{label}</span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredVisits.map((visit) => (
            <VisitRow
              key={visit.id}
              visit={visit}
              setAssignDialogVisit={setAssignDialogVisit}
              setRejectDialogVisit={setRejectDialogVisit}
              currentColumns={currentColumns}
            />
          ))}
        </tbody>
      </table>
      <AssignVisitModal visible={[assignDialogVisit, setAssignDialogVisit]} />
      <RejectVisitModal visible={[rejectDialogVisit, setRejectDialogVisit]} />
    </VerticalLayout>
  );
};

interface VisitRowProps {
  visit: SearchVisits_trippy_searchVisits_visits;
  setAssignDialogVisit: React.Dispatch<
    React.SetStateAction<SearchVisits_trippy_searchVisits_visits | null>
  >;
  setRejectDialogVisit: React.Dispatch<
    React.SetStateAction<SearchVisits_trippy_searchVisits_visits | null>
  >;
  currentColumns: Column[];
}

const VisitRow = ({
  visit,
  setAssignDialogVisit,
  setRejectDialogVisit,
  currentColumns,
}: VisitRowProps) => {
  return (
    <tr style={styles.visitRow} data-testid="visit-row">
      {currentColumns.map((h, j) => {
        const tdStyle = j === 0 ? { paddingLeft: 20 } : {};
        return (
          <td key={j} style={tdStyle}>
            {h.value(visit, setAssignDialogVisit, setRejectDialogVisit)}
          </td>
        );
      })}
    </tr>
  );
};

export default VisitsList;

const styles = {
  columnHeader: {
    height: 40,
  },
  columnHeaderText: {
    fontWeight: '600',
    color: '#8D9AA6',
  },
  cityStateZipText: {
    fontSize: 12,
    color: '#526475',
  },
  visitRow: {
    height: 80,
    borderBottom: '1px solid #C7CFD7',
  },
};
